import { motion, AnimatePresence } from 'framer-motion'
import { X, Flame, MessageCircle, Smile, TrendingDown, AlertTriangle, Sparkles, Zap } from 'lucide-react'
import { useLanguage } from '../contexts/LanguageContext'

interface RoastModeDetailsModalProps {
  isOpen: boolean
  onClose: () => void
}

const RoastModeDetailsModal = ({ isOpen, onClose }: RoastModeDetailsModalProps) => {
  const { t } = useLanguage()

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
          />

          <div className="fixed inset-0 z-50 flex items-start md:items-center justify-center p-4 overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden border border-white/10 my-8"
            >
              <div className="sticky top-0 z-10 bg-gradient-to-r from-orange-600/20 to-red-600/20 border-b border-white/10 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
                      <Flame className="h-8 w-8 text-orange-400" />
                      {t('roast.modal.title')}
                    </h2>
                    <p className="text-gray-400 text-sm mt-1">{t('roast.modal.subtitle')}</p>
                  </div>
                  <button onClick={onClose} className="p-2 rounded-full hover:bg-white/10 transition-colors">
                    <X className="h-6 w-6 text-gray-400" />
                  </button>
                </div>
              </div>

              <div className="overflow-y-auto px-6 py-8 max-h-[calc(90vh-100px)]">
                <div className="mb-8">
                  <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 rounded-2xl p-6 border border-orange-500/20">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-orange-500/20 rounded-xl">
                        <Sparkles className="h-6 w-6 text-orange-400" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2">{t('roast.modal.whatIs')}</h3>
                        <p className="text-gray-300 leading-relaxed">{t('roast.modal.intro')}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6">{t('roast.modal.howItWorks')}</h3>

                  <div className="grid md:grid-cols-2 gap-6">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      className="bg-white/5 rounded-2xl p-6 border border-orange-500/20"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center">
                          <TrendingDown className="h-6 w-6 text-orange-400" />
                        </div>
                        <h4 className="text-lg font-bold text-white">{t('roast.modal.step1.title')}</h4>
                      </div>
                      <p className="text-gray-300 text-sm">{t('roast.modal.step1.description')}</p>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1 }}
                      className="bg-white/5 rounded-2xl p-6 border border-red-500/20"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
                          <Flame className="h-6 w-6 text-red-400" />
                        </div>
                        <h4 className="text-lg font-bold text-white">{t('roast.modal.step2.title')}</h4>
                      </div>
                      <p className="text-gray-300 text-sm">{t('roast.modal.step2.description')}</p>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.2 }}
                      className="bg-white/5 rounded-2xl p-6 border border-yellow-500/20"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center">
                          <MessageCircle className="h-6 w-6 text-yellow-400" />
                        </div>
                        <h4 className="text-lg font-bold text-white">{t('roast.modal.step3.title')}</h4>
                      </div>
                      <p className="text-gray-300 text-sm">{t('roast.modal.step3.description')}</p>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 }}
                      className="bg-white/5 rounded-2xl p-6 border border-pink-500/20"
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 bg-pink-500/20 rounded-xl flex items-center justify-center">
                          <Smile className="h-6 w-6 text-pink-400" />
                        </div>
                        <h4 className="text-lg font-bold text-white">{t('roast.modal.step4.title')}</h4>
                      </div>
                      <p className="text-gray-300 text-sm">{t('roast.modal.step4.description')}</p>
                    </motion.div>
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6">{t('roast.modal.exampleRoasts')}</h3>

                  <div className="space-y-4">
                    {[
                      { emoji: '🔥', text: t('roast.modal.example1') },
                      { emoji: '💰', text: t('roast.modal.example2') },
                      { emoji: '😅', text: t('roast.modal.example3') },
                      { emoji: '🎯', text: t('roast.modal.example4') },
                    ].map((item, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.1 }}
                        className="bg-gradient-to-r from-orange-500/10 to-red-500/10 rounded-2xl p-5 border border-orange-500/20"
                      >
                        <div className="flex items-start gap-4">
                          <span className="text-3xl">{item.emoji}</span>
                          <p className="text-gray-200 italic text-sm">"{item.text}"</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
                    <AlertTriangle className="h-6 w-6 text-yellow-400" />
                    {t('roast.modal.warning')}
                  </h3>
                  
                  <div className="bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-2xl p-6 border border-yellow-500/30">
                    <p className="text-gray-300 leading-relaxed mb-4">{t('roast.modal.warningText')}</p>
                    <p className="text-gray-400 text-sm italic">{t('roast.modal.switchBack')}</p>
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="text-2xl font-bold text-white mb-6">{t('roast.modal.whyUseful')}</h3>
                  
                  <div className="grid md:grid-cols-3 gap-6">
                    {[
                      { icon: Zap, title: t('roast.modal.benefit1.title'), desc: t('roast.modal.benefit1.description') },
                      { icon: Smile, title: t('roast.modal.benefit2.title'), desc: t('roast.modal.benefit2.description') },
                      { icon: MessageCircle, title: t('roast.modal.benefit3.title'), desc: t('roast.modal.benefit3.description') },
                    ].map((item, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: i * 0.1 }}
                        className="bg-gradient-to-br from-orange-500/20 to-red-500/20 rounded-2xl p-6 border border-orange-500/30"
                      >
                        <div className="w-12 h-12 bg-orange-500/30 rounded-xl flex items-center justify-center mb-4">
                          <item.icon className="h-6 w-6 text-orange-400" />
                        </div>
                        <h4 className="text-lg font-bold text-white mb-2">{item.title}</h4>
                        <p className="text-sm text-gray-300">{item.desc}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <div className="bg-gradient-to-r from-orange-600/20 to-red-600/20 rounded-2xl p-8 border border-orange-500/30 text-center">
                  <h3 className="text-2xl font-bold text-white mb-3">{t('roast.modal.cta')}</h3>
                  <p className="text-gray-300 mb-6">{t('roast.modal.ctaDescription')}</p>
                  <button
                    onClick={onClose}
                    className="px-8 py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white font-semibold rounded-xl transition-all"
                  >
                    {t('roast.modal.gotIt')}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  )
}

export default RoastModeDetailsModal
